import { Component, ElementRef } from '@angular/core';
import {
  FormGroup,
  Validators,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
// Third Party
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import {
  Events,
  FirearmTypes,
  ModalID,
  Statements,
} from 'src/app/shared/models/constants';
import {
  BoundBookEntry,
  FromValues,
  ToValues,
} from 'src/app/shared/models/general';
import { Firearm } from 'src/app/shared/models/general';
// Services
import { SharedService } from 'src/app/shared/services/shared.service';
import {
  createDateTimeStamp,
  createEventRecord,
  serialNumberMatchValidator,
} from '../../functions';

@Component({
  selector: 'app-edit-bb-firearm',
  templateUrl: './edit-bb-firearm.component.html',
  styleUrls: ['./edit-bb-firearm.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule],
})
export class EditBbFirearmComponent {
  edit_firearm_form: FormGroup;
  selected_row: Firearm = new Firearm();
  firearm_types = FirearmTypes;
  bound_book_entry: BoundBookEntry;
  serial_editing_blackout: string = 'rgba(255, 255, 255, 0.05)';

  duplicate_enabled_entry = true;
  duplicate_entry = true;

  // Modals
  modalRef: BsModalRef;
  modalConfig = { animated: true };

  constructor(
    protected element: ElementRef,
    private modalService: BsModalService,
    private fb: FormBuilder,
    private sharedService: SharedService,
  ) {
    // Initializing Form
    this.edit_firearm_form = this.fb.group(
      {
        firearm_upc: new FormControl({ value: null, disabled: false }, [
          Validators.maxLength(12),
          Validators.minLength(12),
        ]),
        firearm_type: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        serial_number: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        serial_number2: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        manufacture: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        importer: new FormControl({ value: null, disabled: false }),
        model: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        additional_markings: new FormControl({ value: null, disabled: false }),
        caliber_gauge: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
        edit_note: new FormControl({ value: null, disabled: false }, [
          Validators.required,
        ]),
      },
      { validators: serialNumberMatchValidator },
    );
  }
  ngOnInit(): void {
    this.edit_firearm_form.patchValue({
      firearm_upc: this.bound_book_entry.a_firearm_upc,
      firearm_type: this.bound_book_entry.a_firearm_type,
      serial_number: this.bound_book_entry.a_serial_number,
      serial_number2: this.bound_book_entry.a_serial_number,
      manufacture: this.bound_book_entry.a_manufacture,
      importer: this.bound_book_entry.a_importer,
      model: this.bound_book_entry.a_model,
      additional_markings: this.bound_book_entry.a_additional_markings,
      caliber_gauge: this.bound_book_entry.a_caliber_gauge,
      edit_note: '',
    });
  }
  closeModalClick(): void {
    this.edit_firearm_form.reset();
    this.sharedService.closeModal(ModalID.edit_bb_firearm);
  }
  editFirearmClick() {
    if (this.checkForValuesChanges()) {
      const original_firearm_values = JSON.parse(
        JSON.stringify(this.bound_book_entry),
      );
      const value_differences = this.returnValueChanges();

      // Convert changes array to from_values and to_values objects
      const from_values: any = {};
      const to_values: any = {};
      value_differences.forEach((change) => {
        from_values[change.key] = change.from;
        to_values[change.key] = change.to;
      });

      this.bound_book_entry.a_firearm_upc =
        this.edit_firearm_form.value.firearm_upc;
      this.bound_book_entry.a_firearm_type =
        this.edit_firearm_form.value.firearm_type;
      this.bound_book_entry.a_serial_number =
        this.edit_firearm_form.value.serial_number;
      this.bound_book_entry.a_manufacture =
        this.edit_firearm_form.value.manufacture;
      this.bound_book_entry.a_importer = this.edit_firearm_form.value.importer;
      this.bound_book_entry.a_model = this.edit_firearm_form.value.model;
      this.bound_book_entry.a_additional_markings =
        this.edit_firearm_form.value.additional_markings;
      this.bound_book_entry.a_caliber_gauge =
        this.edit_firearm_form.value.caliber_gauge;
      this.bound_book_entry.notes.push({
        note_type: Events.modify_firearm,
        comment: this.edit_firearm_form.value.edit_note,
        username: this.sharedService.logged_in_user.username,
        dateTime: createDateTimeStamp(),
      });
      this.bound_book_entry.event_log.push(
        createEventRecord(
          Events.modify_firearm,
          from_values,
          to_values,
          this.sharedService.logged_in_user.username,
        ),
      );
      this.sharedService.firearm_to_edit$.next(this.bound_book_entry);
      this.closeModalClick();
    } else {
      this.sharedService.openMessageModal(Statements.no_values_changed);
    }
  }
  checkForValuesChanges() {
    if (
      this.edit_firearm_form.value.firearm_upc !==
        this.bound_book_entry.a_firearm_upc ||
      this.edit_firearm_form.value.firearm_type !==
        this.bound_book_entry.a_firearm_type ||
      this.edit_firearm_form.value.serial_number !==
        this.bound_book_entry.a_serial_number ||
      this.edit_firearm_form.value.manufacture !==
        this.bound_book_entry.a_manufacture ||
      this.edit_firearm_form.value.importer !==
        this.bound_book_entry.a_importer ||
      this.edit_firearm_form.value.model !== this.bound_book_entry.a_model ||
      this.edit_firearm_form.value.additional_markings !==
        this.bound_book_entry.a_additional_markings ||
      this.edit_firearm_form.value.caliber_gauge !==
        this.bound_book_entry.a_caliber_gauge
    ) {
      return true;
    } else {
      return false;
    }
  }
  returnValueChanges(): Array<{ key: string; from: any; to: any }> {
    const changes: Array<{ key: string; from: any; to: any }> = [];

    if (
      this.edit_firearm_form.value.firearm_upc !==
      this.bound_book_entry.a_firearm_upc
    ) {
      changes.push({
        key: 'firearm_upc',
        from: this.bound_book_entry.a_firearm_upc || '',
        to: this.edit_firearm_form.value.firearm_upc,
      });
    }
    if (
      this.edit_firearm_form.value.firearm_type !==
      this.bound_book_entry.a_firearm_type
    ) {
      changes.push({
        key: 'firearm_type',
        from: this.bound_book_entry.a_firearm_type || '',
        to: this.edit_firearm_form.value.firearm_type,
      });
    }
    if (
      this.edit_firearm_form.value.serial_number !==
      this.bound_book_entry.a_serial_number
    ) {
      changes.push({
        key: 'serial_number',
        from: this.bound_book_entry.a_serial_number || '',
        to: this.edit_firearm_form.value.serial_number,
      });
    }
    if (
      this.edit_firearm_form.value.manufacture !==
      this.bound_book_entry.a_manufacture
    ) {
      changes.push({
        key: 'manufacture',
        from: this.bound_book_entry.a_manufacture || '',
        to: this.edit_firearm_form.value.manufacture,
      });
    }
    if (
      this.edit_firearm_form.value.importer !== this.bound_book_entry.a_importer
    ) {
      changes.push({
        key: 'importer',
        from: this.bound_book_entry.a_importer || '',
        to: this.edit_firearm_form.value.importer,
      });
    }
    if (this.edit_firearm_form.value.model !== this.bound_book_entry.a_model) {
      changes.push({
        key: 'model',
        from: this.bound_book_entry.a_model || '',
        to: this.edit_firearm_form.value.model,
      });
    }
    if (
      this.edit_firearm_form.value.additional_markings !==
      this.bound_book_entry.a_additional_markings
    ) {
      changes.push({
        key: 'additional_markings',
        from: this.bound_book_entry.a_additional_markings || '',
        to: this.edit_firearm_form.value.additional_markings,
      });
    }
    if (
      this.edit_firearm_form.value.caliber_gauge !==
      this.bound_book_entry.a_caliber_gauge
    ) {
      changes.push({
        key: 'caliber_gauge',
        from: this.bound_book_entry.a_caliber_gauge || '',
        to: this.edit_firearm_form.value.caliber_gauge,
      });
    }

    return changes;
  }
  serialEdit() {
    if (
      this.edit_firearm_form.value.serial_number &&
      !this.edit_firearm_form.value.serial_number2
    ) {
      this.serial_editing_blackout = '#ffffff';
    }
  }
  compareSerialInputs() {
    if (
      this.edit_firearm_form.value.serial_number &&
      this.edit_firearm_form.value.serial_number2
    ) {
      if (
        this.edit_firearm_form.value.serial_number ===
        this.edit_firearm_form.value.serial_number2
      ) {
        this.serial_editing_blackout = 'rgba(255, 255, 255, 0.05)';
      } else {
        this.serial_editing_blackout = 'rgba(255, 193, 7, 0.2)';
      }
    }
  }
}
